import * as Sentry from "@sentry/react";

type LogLevel = "debug" | "info" | "warn" | "error";

const IS_PROD = process.env["NODE_ENV"] === "production";

const levelOrder: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const configuredLevel: LogLevel =
  (process.env["REACT_APP_LOG_LEVEL"] as LogLevel) || "info";

function shouldLog(level: LogLevel): boolean {
  return levelOrder[level] >= levelOrder[configuredLevel];
}

function format(message?: unknown, ...optionalParams: unknown[]) {
  return [
    "[Event Master]",
    new Date().toISOString(),
    "-",
    message,
    ...optionalParams,
  ];
}

export const logger = {
  // debug and info are no-ops in production to prevent data leakage via browser console
  debug: (message?: unknown, ...optionalParams: unknown[]) => {
    if (!IS_PROD && shouldLog("debug"))
      console.debug(...format(message, ...optionalParams));
  },
  info: (message?: unknown, ...optionalParams: unknown[]) => {
    if (!IS_PROD && shouldLog("info"))
      console.info(...format(message, ...optionalParams));
  },
  warn: (message?: unknown, ...optionalParams: unknown[]) => {
    if (shouldLog("warn")) console.warn(...format(message, ...optionalParams));
  },
  error: (message?: unknown, ...optionalParams: unknown[]) => {
    if (shouldLog("error"))
      console.error(...format(message, ...optionalParams));
  },
};

/**
 * Захват исключений с отправкой в Sentry (production) и логированием (dev)
 * @param error - Ошибка или сообщение об ошибке
 * @param context - Дополнительный контекст (componentStack, source, etc.)
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
) {
  // Всегда логируем в консоль
  logger.error("Exception captured", error, context);

  // В production отправляем в Sentry
  if (process.env["NODE_ENV"] === "production") {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    Sentry.captureException(
      errorObj,
      context != null ? { extra: context } : undefined,
    );
  }
}

/**
 * Установка контекста пользователя для Sentry
 */
export function setUserContext(
  user: { id: string; email?: string; username?: string } | null,
) {
  if (process.env["NODE_ENV"] === "production") {
    Sentry.setUser(user);
  }
}

/**
 * Добавление breadcrumb для отслеживания действий пользователя
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
) {
  if (process.env["NODE_ENV"] === "production") {
    Sentry.addBreadcrumb({
      message,
      category,
      ...(data != null && { data }),
      level: "info",
    });
  }
}
